/**
 * useTeamProjectionData.js
 *
 * Custom hooks for fetching team-wise projection data.
 * Fetches projection data for each team in parallel and transforms for chart rendering.
 *
 * Both quarterly and monthly variants use local state (self-contained).
 * This matches the existing pattern in TeamWiseCurrentQtrProjeectionCard.jsx.
 *
 * Usage:
 * - const { teamData, appliedFilters, isLoading, error } = useQuarterlyTeamProjectionData();
 * - const { teamData, appliedFilters, isLoading, error } = useMonthlyTeamProjectionData();
 */

import { useState, useEffect, useRef } from "react";
import { appService } from "../../App/app.service";
import { fetchFirstLevelTeamNames } from "../rccUtils";
import {
  transformTeamLinearityData,
  buildDefaultFilters,
} from "../RccCards/linearityUtils";

/**
 * Core logic for fetching team projection data.
 * Shared by both quarterly and monthly hooks.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.getFiltersApi - API function to get filters
 * @param {Function} options.getDataApi - API function to get data
 * @param {string} options.periodType - "quarter" or "month"
 * @param {string} options.currentPeriodKey - Key for current period in response (cfq or cfm)
 * @returns {Object} { teamData, appliedFilters, isLoading, error }
 */
function useTeamProjectionDataCore({
  getFiltersApi,
  getDataApi,
  periodType,
  currentPeriodKey,
}) {
  const fetchingRef = useRef(false);

  const [teamData, setTeamData] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip if already fetching
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Fetch filters to get teams list and default values
    getFiltersApi()
      .then((resp) => {
        // Team list comes from the dedicated RCC endpoint (admin/non-admin aware).
        return fetchFirstLevelTeamNames().then((teamNames) => ({
          filtersResp: resp,
          teamNames: Array.isArray(teamNames) ? teamNames : [],
        }));
      })
      .then(({ filtersResp: resp, teamNames }) => {

        if (teamNames.length === 0) {
          console.log("No first-level teams found");
          setTeamData([]);
          setIsLoading(false);
          fetchingRef.current = false;
          return null; // Signal to skip further processing
        }

        // Build base filters using shared utility
        const { filters: rawFilters } = buildDefaultFilters(resp, {
          periodType,
          lookbackPeriods: 4,
        });

        // Team-wise charts should always show first-level teams, so ignore any default team filter.
        const baseFilters = rawFilters.filter((filter) => filter.name !== "team");

        setAppliedFilters(baseFilters);

        // Fetch data for each team in parallel
        const teamDataPromises = teamNames.map((teamName) => {
          return getDataApi([...baseFilters, { name: "team", value: [teamName] }])
            .then((dataResp) => ({
              teamName,
              data: dataResp,
            }))
            .catch((err) => {
              console.error(`Error fetching data for team ${teamName}:`, err);
              const errorMsg =
                err && typeof err === "object"
                  ? err.message || err.error || "Error fetching team data"
                  : err;
              return { teamName, data: null, error: errorMsg };
            });
        });

        return Promise.all(teamDataPromises);
      })
      .then((teamResponses) => {
        // Handle case where no teams were found (null returned from previous then)
        if (!teamResponses) {
          return;
        }

        // Extract current period data from each team's response
        const chartData = teamResponses
          .map(({ teamName, data, error: teamError }) => {
            // Skip if there's an error
            if (teamError) {
              console.log(`Skipping team ${teamName} due to error:`, teamError);
              return null;
            }

            if (!data) {
              console.log(`Skipping team ${teamName} - no data`);
              return null;
            }

            // Check if data is an error response object
            if (data.success === false) {
              console.log(`Skipping team ${teamName} - error response:`, data);
              return null;
            }

            // Check if data.message exists (valid response structure)
            if (!data.message) {
              console.log(
                `Skipping team ${teamName} - no message in data:`,
                data
              );
              return null;
            }

            const linearity = data.message.linearity || [];
            // Get current period from response (cfq for quarterly, cfm for monthly)
            const currentPeriod =
              data.message[currentPeriodKey] ||
              data.message.cfq ||
              data.message.cfm ||
              data.message.currentFiscalQuarter ||
              data.message.currentFiscalMonth;

            if (!currentPeriod) {
              console.log(
                `Skipping team ${teamName} - no current period found`
              );
              return null;
            }

            // Find current period data in linearity array
            // The period field is named "quarter" even for monthly data
            const currentPeriodData = linearity.find(
              (q) => q.quarter === currentPeriod || q.month === currentPeriod
            );

            if (!currentPeriodData) {
              console.log(
                `Skipping team ${teamName} - no data for current period ${currentPeriod}. Available periods:`,
                linearity.map((q) => q.quarter || q.month)
              );
              return null;
            }

            // Transform to chart format using shared utility
            return transformTeamLinearityData(currentPeriodData, teamName);
          })
          .filter(Boolean); // Remove null entries

        setTeamData(chartData);
        setIsLoading(false);
        fetchingRef.current = false;
      })
      .catch((err) => {
        console.error("Error fetching team projection data:", err);
        let errorMessage = "Something went wrong";
        if (typeof err === "string") {
          errorMessage = err;
        } else if (err && typeof err === "object") {
          errorMessage = err.message || err.error || "Something went wrong";
        }
        setError(errorMessage);
        setIsLoading(false);
        fetchingRef.current = false;
      });
  }, [getFiltersApi, getDataApi, periodType, currentPeriodKey]);

  return { teamData, appliedFilters, isLoading, error };
}

/**
 * Hook for fetching quarterly team projection data.
 * Shows current quarter projections broken down by team.
 *
 * @returns {Object} { teamData, appliedFilters, isLoading, error }
 */
export function useQuarterlyTeamProjectionData() {
  return useTeamProjectionDataCore({
    getFiltersApi: appService.getTimeSeriesPredictionv2Filters,
    getDataApi: appService.getTimeSeriesPredictionv2Data,
    periodType: "quarter",
    currentPeriodKey: "cfq",
  });
}

/**
 * Hook for fetching monthly team projection data.
 * Shows current month projections broken down by team.
 *
 * Uses monthly APIs:
 * - getTimeSeriesPredictionByMonthFilters()
 * - getTimeSeriesPredictionByMonthData()
 *
 * @returns {Object} { teamData, appliedFilters, isLoading, error }
 */
export function useMonthlyTeamProjectionData() {
  return useTeamProjectionDataCore({
    getFiltersApi: appService.getTimeSeriesPredictionByMonthFilters,
    getDataApi: appService.getTimeSeriesPredictionByMonthData,
    periodType: "month",
    currentPeriodKey: "cfm",
  });
}
